// 블록 ③ Branded Content Features — 협찬 콘텐츠 캐러셀.
//
// 카드 폭 = aspectRatio(없으면 1.0) × 300. 좌우 스크롤 + 엣지 페이드 + 이전/다음 버튼.
// 페이드는 bg-base 파생(다크 안전).
//
// 플랫폼 탭을 이 블록이 소유한다 — 값이 플랫폼별로 갈리는 곳이 여기이기 때문이다.
// 탭이 붙으면 빈 배열이어도 블록을 숨기지 않는다. 숨기면 다른 플랫폼으로 갈 길이 사라진다.
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { BrandedContentItem, DataMeta, Platform, ChannelSubs } from '../../creatorsApi'
import { InfoTip } from '../InfoTip'
import { PlatformTabs } from '../PlatformTabs'
import { AiJudgedNotice } from '../AiJudgedNotice'
import { channelEmptyText } from '../platformDefault'
import { PLATFORM_LABEL, buildBasisNote, formatCount, formatRate, toWatchUrl } from './format'

const CARD_H = 300

export function BrandedContentCarousel({
  items,
  meta,
  platform,
  onPlatformChange,
  busy,
  channels,
}: {
  items: BrandedContentItem[]
  meta?: DataMeta
  /** 탭을 붙이려면 둘 다 준다. 없으면 탭 없이 렌더(기존 호출부 호환). */
  platform?: Platform
  onPlatformChange?: (platform: Platform) => void
  /** 새 플랫폼 응답 대기 중 — 탭 옆에 표시한다(아래 값은 아직 이전 플랫폼 것이다). */
  busy?: boolean
  /** 사유 판정 입력 — 계정이 없는 채널을 두고 "브랜디드가 없다"고 말하지 않기 위해 필요하다. */
  channels?: ChannelSubs[]
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [fadeL, setFadeL] = useState(0)
  const [fadeR, setFadeR] = useState(1)

  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setFadeL(Math.max(0, Math.min(1, el.scrollLeft / 40)))
    setFadeR(max <= 1 ? 0 : Math.max(0, Math.min(1, (max - el.scrollLeft) / 40)))
  }, [])

  useEffect(() => {
    onScroll()
  }, [onScroll, items])

  function scrollByDir(dir: 1 | -1) {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.7), behavior: 'smooth' })
  }

  // 탭이 있는 블록은 값이 비어도 사라지면 안 된다 — 탭을 눌러 다른 플랫폼으로 갈 길이 막힌다.
  const empty = items.length === 0

  return (
    <section className="mt-8">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-block h-[5px] w-[5px] flex-none bg-brand-pink" aria-hidden />
        <h3 className="font-display text-[20px] font-bold tracking-[-0.01em] text-fg-1">
          BRANDED CONTENT FEATURES
        </h3>
        {platform && onPlatformChange && (
          <PlatformTabs value={platform} onChange={onPlatformChange} label="협찬 콘텐츠 플랫폼" busy={busy} />
        )}
        {/* 상위 N만 보여주는 블록은 선별 기준을 ⓘ로 밝힌다.
            개수는 **선별 기준값 12 로 고정**한다(사용자 확정). 실제 렌더 수에서 파생하면 그건
            기준이 아니라 결과를 말하는 문구가 된다 — 협찬 콘텐츠가 없는 플랫폼 탭에서
            "상위 0개를 보여줍니다"가 뜬다. Profile 의 대표 콘텐츠 고지와도 같은 값이다. */}
        <InfoTip
          label="Branded content 선별 기준"
          text="협찬 콘텐츠를 확정 조회수 순으로 정렬해 상위 12개를 보여줍니다."
        />
        {/* 집계 고지 — 이 블록의 수치가 어디서·언제·어느 창으로 나온 값인지 1줄로 밝힌다(A2).
            platform 을 넘기지 않는다: AI 판독 고지는 아래 전용 줄이 이미 말하고 있어 중복이고,
            그 조각이 붙는 IG·TikTok 탭에서만 문구가 길어져 제목 행에서 밀려 내려갔다. */}
        <span className="ml-auto break-keep font-text text-[11px] text-fg-3">
          {buildBasisNote('협찬 콘텐츠 · 확정 조회수 상위', meta)}
        </span>
      </div>

      <AiJudgedNotice className="mb-4" />

      {/* 빈 채널에서도 **자리를 남긴다** — 영역이 통째로 사라지면 탭을 눌렀는데 아무 일도
          안 난 것처럼 보이고, 그 채널에 협찬이 없다는 사실도 화면에서 지워진다. */}
      {empty && (
        <div className="border border-dashed border-line-soft bg-surface-soft p-8 text-center font-text text-[13px] text-fg-3">
          {channelEmptyText(platform ?? 'youtube', channels)}
        </div>
      )}

      <div className="relative" hidden={empty}>
        <div ref={scrollRef} onScroll={onScroll} className="flex gap-4 overflow-x-auto pb-4">
          {items.map((f) => {
            // 시안(Creators.dc.html) 카드 비율 범위 0.72~1.5 로 클램프 — BE 원값(9/16·16/9)을
            // 그대로 쓰면 롱폼 카드가 533px 로 시안(최대 450px)보다 넓어진다. 썸네일은 cover 크롭.
            const ar = Math.min(1.5, Math.max(0.72, f.aspectRatio ?? 1))
            const w = Math.round(ar * CARD_H)
            const inner = (
              <>
                {/* 썸네일 null 이면 다크 폴백(Profile 대표 콘텐츠와 동일) — 빈 회색 박스 방지.
                    폭·높이는 aspectRatio 기반으로 이미 고정이라 그리드가 무너지지 않는다. */}
                <div className="relative overflow-hidden bg-surface-ink" style={{ height: CARD_H }}>
                  {f.thumbnailUrl && (
                    <img
                      src={f.thumbnailUrl}
                      alt=""
                      aria-hidden
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  )}
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0) 38%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.42) 100%)',
                    }}
                  />
                  <span className="absolute left-2.5 top-2.5 bg-black/50 px-2 py-1 font-text text-[11px] font-semibold tracking-[0.08em] text-white backdrop-blur-[6px]">
                    {PLATFORM_LABEL[f.platform]}
                  </span>
                </div>
                {/* 제목은 **있을 때만** 자리를 잡는다 — Instagram 게시물처럼 제목이 없는 항목에서
                    빈 2줄이 남아 카드가 텅 빈 것처럼 보였다. 제목이 있으면 2줄분(33px)을 예약해
                    1줄짜리와 2줄짜리의 아래 요소 위치가 어긋나지 않게 한다.
                    브랜드 줄(15px)은 계속 예약한다 — 그쪽은 대부분 값이 있어 빼면 오히려 들쭉날쭉해진다. */}
                {f.title?.trim() ? (
                  <div className="mt-[9px] h-[33px] overflow-hidden font-text text-[12px] font-semibold leading-[1.35] text-fg-1 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                    {f.title}
                  </div>
                ) : null}
                <div className="mt-[5px] h-[15px] truncate font-text text-[11px] leading-[15px] text-brand-pink">
                  {[f.brand, f.product].filter(Boolean).join(' · ')}
                </div>
                <div className="mt-2 flex gap-4 border-t border-line-hairline pt-2">
                  <div>
                    <div className="font-text text-[11px] tracking-[0.06em] text-fg-3">조회수</div>
                    {/* 0 은 "—" 로 그린다 — Instagram 이미지·캐러셀 게시물은 플랫폼이 조회수를
                        주지 않아 0 으로 내려온다(영상에만 준다). 그대로 "0"을 그리면 아무도
                        보지 않은 게시물로 읽힌다. 없는 값과 0 을 같은 기호로 묶는다. */}
                    <div className="whitespace-nowrap font-display text-[16px] font-bold leading-[1.1] tabular-nums text-fg-1">
                      {f.views ? formatCount(f.views) : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="font-text text-[11px] tracking-[0.06em] text-fg-3">시청률</div>
                    <div className="whitespace-nowrap font-display text-[16px] font-bold leading-[1.1] tabular-nums text-fg-1">
                      {formatRate(f.watchRate)}
                    </div>
                  </div>
                </div>
              </>
            )
            // url 있으면 카드 전체를 새 탭 링크로(시각은 그대로, 클릭만 부여). 없으면 기존 비클릭.
            return f.url ? (
              <a
                key={f.id}
                href={toWatchUrl(f.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-none cursor-pointer"
                style={{ width: w }}
              >
                {inner}
              </a>
            ) : (
              <article key={f.id} className="flex-none" style={{ width: w }}>
                {inner}
              </article>
            )
          })}
        </div>

        {/* 엣지 페이드 (bg-base 파생 — 다크 안전) */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-20"
          style={{
            background: 'linear-gradient(90deg, var(--bg-base), transparent)',
            opacity: fadeL,
          }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-20"
          style={{
            background: 'linear-gradient(270deg, var(--bg-base), transparent)',
            opacity: fadeR,
          }}
        />

        <button
          type="button"
          onClick={() => scrollByDir(-1)}
          aria-label="이전"
          className="absolute left-3 top-[150px] z-[2] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line-hairline bg-surface-base shadow-lf-2"
          style={{ opacity: fadeL, pointerEvents: fadeL > 0 ? 'auto' : 'none' }}
        >
          <ChevronLeft className="h-5 w-5 text-fg-1" strokeWidth={2.4} />
        </button>
        <button
          type="button"
          onClick={() => scrollByDir(1)}
          aria-label="다음"
          className="absolute right-3 top-[150px] z-[2] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line-hairline bg-surface-base shadow-lf-2"
          style={{ opacity: fadeR, pointerEvents: fadeR > 0 ? 'auto' : 'none' }}
        >
          <ChevronRight className="h-5 w-5 text-fg-1" strokeWidth={2.4} />
        </button>
      </div>
    </section>
  )
}
